/**
 * voice-v8 — Module-Test-Tab.
 *
 * Pre.152: User-friendly Refactor (Live-Bug Martin pre.151):
 *   - Dedupe: 1 Wetter statt 2 (Wien/Hollenthon), 1 PDF-Knopf statt 4
 *     verschiedene Posteingang-Varianten, 1 Notiz-Knopf statt 2 (memory_save
 *     für Kontakt + Notiz). Klare Kernelemente.
 *   - Jedes Modul hat ein SVG-Icon. Tool-Name als Sublabel unten weg —
 *     der Bot-User braucht ihn nicht.
 *   - Tooltips mit Prompt bleiben (Title-Attribut).
 *
 * Klick → schickt den Prompt als User-Message via onSendText. Funktioniert
 * in beiden Modi (Voice + Chat-HTTP).
 */

import type { VoiceClientLike } from "./voice-client.js";
import { buildCloseButton } from "./sheet-ui.js";

type Category =
  | "Allgemein"
  | "Erinnerungen"
  | "Mail & PDF"
  | "Kalender & Drive"
  | "Mediathek"
  | "Musik & Web"
  | "Browser";

interface ToolTest {
  category: Category;
  tool: string;
  label: string;
  prompt: string;
  icon: string;
}

// SVG-Path-d für jedes Tool. 24x24 viewBox, fill="currentColor".
const ICON: Record<string, string> = {
  weather:
    "M6 19a5 5 0 0 1-1-9.91A6 6 0 1 1 17 9c0 .14 0 .28-.02.42A4.5 4.5 0 0 1 17.5 19H6z",
  timer:
    "M15 1H9v2h6V1zm-3 5a9 9 0 1 0 9 9 9 9 0 0 0-9-9zm0 16a7 7 0 1 1 7-7 7 7 0 0 1-7 7zm-1-12h2v6h-2zM19.03 7.39l-1.42-1.42-2.12 2.12 1.41 1.41 2.13-2.11z",
  search:
    "M15.5 14h-.79l-.28-.27a6.5 6.5 0 1 0-.7.7l.27.28v.79l5 5 1.49-1.49zm-6 0a4.5 4.5 0 1 1 4.5-4.5 4.5 4.5 0 0 1-4.5 4.5z",
  memory_save:
    "M14.06 9 15 9.94 5.92 19H5v-.92zm3.6-6a1 1 0 0 0-.71.29l-1.83 1.83 3.75 3.75 1.83-1.83a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-.7-.29zm-3.6 3.19L3 17.25V21h3.75L17.81 9.94z",
  memory_search:
    "M11.5 14H10.71l-.28-.27a6.5 6.5 0 1 0-.7.7l.27.28v.79l3 3 1.49-1.49zM6.5 14a4.5 4.5 0 1 1 4.5-4.5 4.5 4.5 0 0 1-4.5 4.5zM18 8h-2V5a3 3 0 0 0-6 0v3H8V5a5 5 0 0 1 10 0z",
  call: "M19.23 15.26 16.69 14.97a1.99 1.99 0 0 0-1.81.6l-1.84 1.85a15.04 15.04 0 0 1-6.59-6.6l1.86-1.85a2 2 0 0 0 .59-1.81L8.61 4.7A2 2 0 0 0 6.62 3H4.43a2 2 0 0 0-2 2.07A17 17 0 0 0 18.93 21.57a2 2 0 0 0 2.07-2v-2.32a2 2 0 0 0-1.77-1.99z",
  mail: "M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4-8 5-8-5V6l8 5 8-5z",
  mail_send: "M2.01 21 23 12 2.01 3 2 10l15 2-15 2z",
  pdf: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm2 16H8v-2h8zm0-4H8v-2h8zm-3-5V3.5L18.5 9z",
  calendar:
    "M19 3h-1V1h-2v2H8V1H6v2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm0 16H5V8h14z",
  calendar_create:
    "M19 3h-1V1h-2v2H8V1H6v2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm0 16H5V8h14zm-7-9h2v3h3v2h-3v3h-2v-3H9v-2h3z",
  cloud:
    "M19.35 10.04A7.49 7.49 0 0 0 12 4a7.5 7.5 0 0 0-6.97 4.74A6 6 0 0 0 6 20.5h13a4.5 4.5 0 0 0 .35-9.46zM10 17l-3.5-4.5 2.5-3 2 2.5L13.5 9 17 13.5z",
  read_file:
    "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm-1 14H8v-2h5zm3-4H8v-2h8zm-3-5V3.5L18.5 9z",
  image:
    "M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2zm-12.5-5.5 2.5 3.01L14.5 12l4.5 6H5z",
  music: "M12 3v10.55a4 4 0 1 0 2 3.45V7h4V3z",
  link: "M3.9 12a3.1 3.1 0 0 1 3.1-3.1h4V7H7a5 5 0 0 0 0 10h4v-1.9H7A3.1 3.1 0 0 1 3.9 12zM8 13h8v-2H8zm9-6h-4v1.9h4a3.1 3.1 0 0 1 0 6.2h-4V17h4a5 5 0 0 0 0-10z",
  browser_tab:
    "M21 3H3a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm0 16H3V7h18z",
  browser_text:
    "M21 3H3a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm0 16H3V7h18zM6 9h12v2H6zm0 4h12v2H6z",
  camera:
    "M12 15.2a3.2 3.2 0 1 1 3.2-3.2 3.2 3.2 0 0 1-3.2 3.2zm5-11h-3.17l-1.84-2H8.01L6.17 4.2H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-12a2 2 0 0 0-2-2z",
};

const TOOL_TESTS: ReadonlyArray<ToolTest> = [
  // --- Allgemein ----------------------------------------------------------
  {
    category: "Allgemein",
    tool: "weather",
    label: "Wetter",
    prompt: "Wie wird das Wetter heute?",
    icon: "weather",
  },
  {
    category: "Allgemein",
    tool: "timer_local",
    label: "Timer",
    prompt: "Stell einen Timer auf 30 Sekunden für Eier kochen.",
    icon: "timer",
  },
  {
    category: "Allgemein",
    tool: "web_search",
    label: "Web-Suche",
    prompt: "Recherchier kurz im Internet, was ein Quantencomputer ist.",
    icon: "search",
  },

  // --- Erinnerungen ------------------------------------------------------
  {
    category: "Erinnerungen",
    tool: "memory_save",
    label: "Notiz speichern",
    prompt: "Merk dir bitte: Ich teste gerade die Module von voice-v8.",
    icon: "memory_save",
  },
  {
    category: "Erinnerungen",
    tool: "memory_search",
    label: "Notizen suchen",
    prompt: "Was hast du dir gemerkt?",
    icon: "memory_search",
  },
  {
    category: "Erinnerungen",
    tool: "call",
    label: "Anruf",
    prompt: "Ruf bitte Tante Erna an.",
    icon: "call",
  },

  // --- Mail & PDF --------------------------------------------------------
  {
    category: "Mail & PDF",
    tool: "send_mail",
    label: "Mail senden",
    prompt:
      "Schick eine kurze Test-Mail an office@ssi.at mit dem Betreff voice-v8 test.",
    icon: "mail_send",
  },
  {
    category: "Mail & PDF",
    tool: "gmail_search",
    label: "Gmail suchen",
    prompt: "Such die letzten 5 Mails von heute aus dem Gmail-Postfach.",
    icon: "mail",
  },
  {
    category: "Mail & PDF",
    tool: "make_pdf",
    label: "PDF erzeugen",
    prompt:
      "Erzeuge ein PDF mit Titel 'voice-v8 Test' und einer Begrüßung sowie einer Bullet-Liste mit drei Punkten.",
    icon: "pdf",
  },

  // --- Kalender & Drive --------------------------------------------------
  {
    category: "Kalender & Drive",
    tool: "calendar_list",
    label: "Termine",
    prompt: "Was hab ich diese Woche für Termine?",
    icon: "calendar",
  },
  {
    category: "Kalender & Drive",
    tool: "calendar_create",
    label: "Termin anlegen",
    prompt:
      "Trag morgen 14 Uhr Zahnarzt-Termin ein, eine Stunde, in Wiener Neustadt.",
    icon: "calendar_create",
  },
  {
    category: "Kalender & Drive",
    tool: "drive_search",
    label: "Drive durchsuchen",
    prompt: "Such in meinem Drive nach Dateien zum Thema Rechnung.",
    icon: "cloud",
  },

  // --- Mediathek ---------------------------------------------------------
  {
    category: "Mediathek",
    tool: "read_file",
    label: "Datei lesen",
    prompt:
      "Die zuletzt hochgeladene Datei aus meiner Mediathek bitte einlesen und kurz zusammenfassen.",
    icon: "read_file",
  },
  {
    category: "Mediathek",
    tool: "generate_image",
    label: "Bild erzeugen",
    prompt: "Erzeuge ein Bild von einem Affen auf einem Schleifstein.",
    icon: "image",
  },

  // --- Musik & Web -------------------------------------------------------
  {
    category: "Musik & Web",
    tool: "fokus_videos",
    label: "Videos zum Thema",
    prompt: "Zeig mir, wie man einen Nagel richtig in die Wand schlägt.",
    // ⚠️ "video" gibt es in icons.ts NICHT — das Feld ist `string`, tsc
    // hätte es durchgelassen und das Symbol wäre leer geblieben.
    icon: "play",
  },
  {
    category: "Musik & Web",
    tool: "play_music",
    label: "Musik abspielen",
    prompt: "Spiel mir Reinhard Fendrich Midlife Crisis.",
    icon: "music",
  },
  {
    category: "Musik & Web",
    tool: "open_url",
    label: "URL öffnen",
    prompt: "Öffne YouTube mit Suche Quantenmechanik.",
    icon: "link",
  },

  // --- Browser-Extension -------------------------------------------------
  {
    category: "Browser",
    tool: "browser_get_active_tab",
    label: "Aktiver Tab",
    prompt: "Welche Webseite hab ich gerade offen?",
    icon: "browser_tab",
  },
  {
    category: "Browser",
    tool: "browser_get_page_text",
    label: "Seite lesen",
    prompt: "Lies mir bitte die aktuelle Seite zusammen.",
    icon: "browser_text",
  },
  {
    category: "Browser",
    tool: "browser_screenshot",
    label: "Screenshot",
    prompt: "Mach einen Screenshot vom aktiven Tab.",
    icon: "camera",
  },
];

interface ToolsViewOptions {
  /** Schickt einen Prompt als User-Message — funktioniert in beiden Modi
   * (Voice + Chat-HTTP). Im app.ts mit handleTextSubmit-Logik verbunden. */
  onSendText: (text: string) => Promise<void>;
  /** Wird gerufen wenn der Send erfolgreich war — Drawer kann sich
   * dann selbst schließen, damit der Chat sofort sichtbar wird. */
  onAfterRun?: () => void;
  /** Close-Button im Drawer-Header. */
  onClose?: () => void;
}

const CATEGORY_ORDER: ReadonlyArray<Category> = [
  "Allgemein",
  "Erinnerungen",
  "Mail & PDF",
  "Kalender & Drive",
  "Mediathek",
  "Musik & Web",
  "Browser",
];

const SVG_NS = "http://www.w3.org/2000/svg";

function makeIcon(iconKey: string): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "22");
  svg.setAttribute("height", "22");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("fill", "currentColor");
  path.setAttribute("d", ICON[iconKey] ?? ICON.search ?? "");
  svg.appendChild(path);
  return svg;
}

export class ToolsView {
  private statusEl!: HTMLElement;

  constructor(
    root: HTMLElement,
    _client: VoiceClientLike,
    _isConnected: () => boolean,
    private opts: ToolsViewOptions,
  ) {
    while (root.firstChild) root.removeChild(root.firstChild);
    root.classList.add("tools-view");

    // --- Side-Drawer-Header mit Titel + Close-Button (X) ---
    const head = document.createElement("header");
    head.className = "side-drawer-head";
    const title = document.createElement("h2");
    title.className = "side-drawer-title";
    title.textContent = "Module";
    head.appendChild(title);
    const close = buildCloseButton(() => this.opts.onClose?.());
    head.appendChild(close);
    root.appendChild(head);

    const body = document.createElement("div");
    body.className = "tools-body";

    // --- Sections per Kategorie ---
    for (const cat of CATEGORY_ORDER) {
      const tests = TOOL_TESTS.filter((t) => t.category === cat);
      if (tests.length === 0) continue;
      const section = document.createElement("section");
      section.className = "tools-section";
      section.dataset.cat = cat;
      const heading = document.createElement("h3");
      heading.className = "tools-section-title";
      heading.textContent = cat;
      section.appendChild(heading);

      const grid = document.createElement("div");
      grid.className = "tools-grid";
      for (const test of tests) {
        const card = document.createElement("button");
        card.type = "button";
        card.className = "tools-card";

        const iconWrap = document.createElement("span");
        iconWrap.className = "tools-card-icon";
        iconWrap.appendChild(makeIcon(test.icon));
        card.appendChild(iconWrap);

        const cardLabel = document.createElement("div");
        cardLabel.className = "tools-card-label";
        cardLabel.textContent = test.label;
        card.appendChild(cardLabel);

        // Prompt nur als title-Tooltip — spart vertikalen Platz.
        card.title = test.prompt;

        card.addEventListener("click", () => void this.run(test));
        grid.appendChild(card);
      }
      section.appendChild(grid);
      body.appendChild(section);
    }

    this.statusEl = document.createElement("div");
    this.statusEl.className = "tools-status";
    body.appendChild(this.statusEl);

    root.appendChild(body);
  }

  private async run(test: ToolTest): Promise<void> {
    try {
      await this.opts.onSendText(test.prompt);
      this.flash(`gesendet: ${test.label}`, false);
    } catch (err) {
      this.flash(
        `Fehler: ${err instanceof Error ? err.message : String(err)}`,
        true,
      );
    }
  }

  private flash(msg: string, isError: boolean): void {
    this.statusEl.textContent = msg;
    this.statusEl.dataset.kind = isError ? "error" : "ok";
    setTimeout(() => {
      this.statusEl.textContent = "";
      delete this.statusEl.dataset.kind;
    }, 2500);
  }
}
